import React, { forwardRef, useState, useEffect, useRef } from 'react';
import './MessageBubble.css';
import CustomCarousel from '../ChatBody/CustomCarousel';
import ReactMarkdown from 'react-markdown';
import { formatWhatsAppTimestamp } from '../../utils/utils';

// Custom link component for ReactMarkdown
const CustomLink = ({ href, children }) => {
  // Ensure href is properly formatted
  let formattedHref = href;
  
  // If there's a trailing quotation mark, remove it
  if (formattedHref.endsWith('"')) {
    formattedHref = formattedHref.slice(0, -1);
  }
  
  // Ensure URL is absolute
  if (!/^https?:\/\//i.test(formattedHref)) {
    formattedHref = 'https://' + formattedHref;
  }
  
  return (
    <a 
      href={formattedHref}
      className="enhanced-link" 
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
      <span className="material-icons mia-link-icon">open_in_new</span>
    </a>
  );
};

const MessageBubble = forwardRef(({ message, className, isHumanControlled, onSendMessage }, ref) => {
  const { text, sender, timestamp, carouselItems, type, categories, replyTo } = message;
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    licensePlate: '',
    notes: '',
    preferredTime: '',
    customerType: 'private',
    timeRange: 'mattina',
    calendarDate: '',
    calendarTime: ''
  });
  const [messageSent, setMessageSent] = useState(false);
  const [useMarkdown, setUseMarkdown] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);

  // Add state for second engagement features
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneSubmitted, setPhoneSubmitted] = useState(false);

  // Swipe-to-reply state
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const messageBubbleRef = useRef(null);
  const isMobileRef = useRef(window.innerWidth <= 768);
  const SWIPE_THRESHOLD = 50; // Minimum swipe distance to trigger reply
  const MAX_SWIPE_OFFSET = 80; // Maximum swipe distance

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      isMobileRef.current = window.innerWidth <= 768;
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle swipe-to-reply
  const handleTouchStart = (e) => {
    // Only enable swipe on mobile and for bot messages without carousels
    if (!isMobileRef.current || sender === 'user' || carouselItems?.length > 0) {
      return;
    }
    
    // Check if touch started on a link or interactive element
    const target = e.target;
    if (target.tagName === 'A' || target.closest('a') || target.closest('button')) {
      return;
    }
    
    const touch = e.touches[0];
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    setIsSwiping(true);
  };

  const handleTouchMove = (e) => {
    if (!isSwiping || !isMobileRef.current || sender === 'user' || carouselItems?.length > 0) {
      return;
    }

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = Math.abs(touch.clientY - touchStartYRef.current);
    
    // Only allow swiping left (negative deltaX) for bot messages
    // Prevent if vertical movement is greater (user is scrolling)
    if (deltaX < 0 && deltaY < Math.abs(deltaX) * 0.5) {
      e.preventDefault(); // Prevent scrolling while swiping
      const offset = Math.max(-MAX_SWIPE_OFFSET, deltaX);
      setSwipeOffset(offset);
    }
  };

  const handleTouchEnd = () => {
    if (!isSwiping || !isMobileRef.current) {
      return;
    }

    // If swiped enough, trigger reply
    if (Math.abs(swipeOffset) >= SWIPE_THRESHOLD) {
      handleReply();
    }

    // Reset swipe state
    setSwipeOffset(0);
    setIsSwiping(false);
    touchStartXRef.current = 0;
    touchStartYRef.current = 0;
  };

  const handleReply = () => {
    // Extract plain text from message
    const plainText = text ? text.replace(/<[^>]*>/g, '').trim() : '';
    if (!plainText) return;

    // Dispatch event to set reply message in ChatBody
    const replyEvent = new CustomEvent('chatReply', {
      detail: {
        message: {
          id: message.id || message._id,
          text: plainText,
          sender: sender,
          timestamp: timestamp
        }
      },
      bubbles: true
    });
    document.dispatchEvent(replyEvent);
    
    // Focus the input field
    const input = document.querySelector('.mia-message-input');
    if (input) {
      input.focus();
    }
  };

  // Handler for phone number submission
  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    if (phoneNumber.trim() && onSendMessage) {
      // Send phone number as a message
      const phoneMessage = `Il mio numero di telefono è: ${phoneNumber}`;
      
      onSendMessage(phoneMessage);
      setPhoneSubmitted(true);
      console.log('☎️ MessageBubble: Phone number submitted:', phoneNumber);
    }
  };

  // Handler for WhatsApp CTA click
  const handleWhatsAppClick = () => {
    if (!message.whatsappNumber) {
      console.warn('📱 WhatsApp number not available');
      return;
    }
    
    // Use the context-aware message if available, otherwise fall back to conversation history
    let whatsappText = '';
    
    if (message.whatsappMessage) {
      // Use the pre-generated context-aware message
      whatsappText = message.whatsappMessage;
      console.log('📱 MessageBubble: Using context-aware WhatsApp message');
    } else {
      // Fall back to conversation history approach
      const conversationHistory = message.conversationHistory || [];
      whatsappText = 'Ciao, scrivo dalla chat del sito web:\n\n';
      
      conversationHistory.forEach((msg, index) => {
        whatsappText += `${index + 1}. ${msg}\n`;
      });
      
      // Get chat ID for MIA reference
      const chatId = localStorage.getItem("aitomotivelab_personalData_chat_id");
      const miaId = chatId && chatId !== "undefined" && chatId !== "null" ? ` (MIA_ID=${chatId})` : '';
      whatsappText += miaId;
      
      console.log('📱 MessageBubble: Using conversation history fallback');
    }
    
    const whatsappUrl = `https://wa.me/${message.whatsappNumber}?text=${encodeURIComponent(whatsappText)}`;
    
    console.log('📱 MessageBubble: Opening WhatsApp with message:', whatsappText);
    window.open(whatsappUrl, '_blank');
  };
  
  // Create refs for form inputs
  const formRef = useRef(null);
  const activeInputRef = useRef(null);
  const brandInputRef = useRef(null);
  const modelInputRef = useRef(null);
  const licensePlateInputRef = useRef(null);
  const preferredTimeInputRef = useRef(null);
  const notesInputRef = useRef(null);
  const submitButtonRef = useRef(null);
  const calendarDateRef = useRef(null);
  const calendarTimeRef = useRef(null);
  
  // Effect to focus on the first input field when a subcategory is selected
  useEffect(() => {
    if (selectedCategory && selectedCategory.requiresForm && selectedSubcategory && !messageSent) {
      // Get the fields for the selected subcategory
      const fields = selectedSubcategory.fields || [];
      
      // Focus on the first input field after a short delay to allow rendering
      setTimeout(() => {
        if (fields.includes('brand') && brandInputRef.current) {
          brandInputRef.current.focus();
        } else if (fields.includes('notes') && notesInputRef.current) {
          notesInputRef.current.focus();
        }
      }, 100);
    }
  }, [selectedSubcategory, messageSent]);
  
  
  // Generate available times for the selected date (15-minute intervals)
  useEffect(() => {
    if (selectedDate) {
      // Generate times from 9:00 to 18:45 in 15-minute intervals
      const times = [];
      for (let hour = 9; hour < 19; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          // Skip 18:15, 18:30, 18:45
          if (hour === 18 && minute > 0) continue;
          
          const formattedHour = hour.toString().padStart(2, '0');
          const formattedMinute = minute.toString().padStart(2, '0');
          times.push(`${formattedHour}:${formattedMinute}`);
        }
      }
      setAvailableTimes(times);
    }
  }, [selectedDate]);
  
  // Function to check if text contains markdown that needs ReactMarkdown
  const containsComplexMarkdown = (text) => {
    if (!text) return false;
    
    // Check for heading syntax, URLs, links, tables, or code blocks
    const patterns = [
      /^#+\s+.+$/m,                    // Headers
      /\[.+\]\(.+\)/,                  // Links
      /https?:\/\/\S+/,                // URLs
      /^(?:\s*[-*+]|\s*\d+\.)\s+.+$/m, // Lists
      /`{1,3}[^`]+`{1,3}/,             // Inline code
      /^>\s+.+$/m                      // Blockquotes
    ];
    
    return patterns.some(pattern => pattern.test(text));
  };
  
  // Use effect to set markdown mode on mount and when text changes
  useEffect(() => {
    if (text) {
      setUseMarkdown(containsComplexMarkdown(text));
    }
  }, [text]);
  
  // Function to convert markdown-like syntax to HTML with better list handling
  const formatText = (text) => {
    if (!text) return '';
    
    // Special handling for welcome message and system messages
    if (message.id === 'welcome' || type === 'system') {
      console.log('Directly returning HTML for welcome/system message');
      return text;
    }
    
    // If useMarkdown is already set, just return the raw text for ReactMarkdown
    if (useMarkdown) {
      return text;
    }
    
    // Check if the text already contains HTML tags
    const containsHtml = /<\/?[a-z][\s\S]*>/i.test(text);
    
    if (containsHtml) {
      // For text that already contains HTML, we need to clean it up
      // First, fix broken list structures
      let cleanedHtml = text;
      
      // Fix common issues with nested lists
      cleanedHtml = cleanedHtml.replace(/<\/li>\s*<ul>/g, '<ul>');
      cleanedHtml = cleanedHtml.replace(/<\/li>\s*<ol>/g, '<ol>');
      
      // Enhance URLs in HTML content
      cleanedHtml = cleanedHtml.replace(
        /<a\s+href="([^"]+)"([^>]*)>([^<]+)<\/a>/g, 
        '<a href="$1" class="enhanced-link" target="_blank" rel="noopener noreferrer">$3 <span class="material-icons mia-link-icon">open_in_new</span></a>'
      );
      
      // Fix car listing format - convert incorrectly formatted lists to proper structure
      if (cleanedHtml.includes('Audi') && cleanedHtml.includes('Prezzo:') && cleanedHtml.includes('Anno:')) {
        // This is likely a car listing - convert to a better format
        
        // Remove any existing list tags that might be causing issues
        cleanedHtml = cleanedHtml.replace(/<\/?[ou]l>|<\/?li>/g, '');
        
        // Split by car model (assuming each car starts with "Audi")
        const parts = cleanedHtml.split(/<strong>Audi|Audi/);
        
        if (parts.length > 1) {
          let result = parts[0]; // Keep any intro text
          
          // Start a div container for the car listings
          result += '<div class="car-listings">';
          
          // Process each car entry
          for (let i = 1; i < parts.length; i++) {
            const carDetails = '<strong>Audi' + parts[i];
            
            // Create a styled div for each car
            result += `
              <div class="chat-car-item">
                ${carDetails.replace(/Prezzo:/, '<br><strong>Prezzo:</strong>')
                           .replace(/Anno:/, '<br><strong>Anno:</strong>')
                           .replace(/Chilometraggio:/, '<br><strong>Chilometraggio:</strong>')}
              </div>
            `;
          }
          
          result += '</div>';
          return result;
        }
      }
      
      return cleanedHtml;
    }
    
    // For regular markdown text (not HTML)
    
    // Convert bold text (**text**)
    let formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convert italic text (*text*)
    formattedText = formattedText.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    
    // Convert markdown headings (### Heading)
    formattedText = formattedText.replace(/^###\s+(.*?)$/gm, '<h3>$1</h3>');
    formattedText = formattedText.replace(/^##\s+(.*?)$/gm, '<h2>$1</h2>');
    formattedText = formattedText.replace(/^#\s+(.*?)$/gm, '<h1>$1</h1>');
    
    // Convert links with enhanced styling ([text](url))
    formattedText = formattedText.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g, 
      '<a href="$2" class="enhanced-link" target="_blank" rel="noopener noreferrer">$1 <span class="material-icons mia-link-icon">open_in_new</span></a>'
    );
    
    // More careful URL matching to avoid capturing trailing characters
    // This regex looks for URLs that are bounded by whitespace, start of string, or end of string
    formattedText = formattedText.replace(
      /(^|\s)(https?:\/\/[^\s"]+)(?=\s|$|"|\.|\)|\]|,|;|<|\?|!)/g, 
      '$1<a href="$2" class="enhanced-link" target="_blank" rel="noopener noreferrer">$2 <span class="material-icons mia-link-icon">open_in_new</span></a>'
    );
    
    // Handle bullet lists (lines starting with - or *)
    const bulletListRegex = /(?:^|\n)((?:[ \t]*[-*][ \t]+.+(?:\n|$))+)/g;
    formattedText = formattedText.replace(bulletListRegex, (match, listBlock) => {
      const items = listBlock.split(/\n/).filter(line => /^[ \t]*[-*][ \t]+/.test(line));
      const listItems = items.map(item => {
        const content = item.replace(/^[ \t]*[-*][ \t]+/, '');
        return `<li>${content}</li>`;
      }).join('');
      return `<ul class="formatted-list">${listItems}</ul>`;
    });
    
    // Handle numbered lists (lines starting with number.)
    const numberedListRegex = /(?:^|\n)((?:[ \t]*\d+\.[ \t]+.+(?:\n|$))+)/g;
    formattedText = formattedText.replace(numberedListRegex, (match, listBlock) => {
      const items = listBlock.split(/\n/).filter(line => /^[ \t]*\d+\.[ \t]+/.test(line));
      const listItems = items.map(item => {
        const content = item.replace(/^[ \t]*\d+\.[ \t]+/, '');
        return `<li>${content}</li>`;
      }).join('');
      return `<ol class="formatted-list">${listItems}</ol>`;
    });
    
    // Convert line breaks to <br> tags
    formattedText = formattedText.replace(/\n/g, '<br>');
    
    return formattedText;
  };
  
  const handleSendMessage = (message) => {
    console.log("Sending message:", message);
    
    // Try different methods to send the message
    // Method 1: Use the global sendMessage function if available
    if (typeof window.sendMessage === 'function') {
      window.sendMessage(message);
      console.log("Message sent using window.sendMessage");
      setMessageSent(true);
      return;
    }
    
    // Method 2: Find the ChatBody component's onSendMessage function
    if (window.chatBodyInstance && typeof window.chatBodyInstance.handleSendMessage === 'function') {
      window.chatBodyInstance.handleSendMessage(message);
      console.log("Message sent using chatBodyInstance.handleSendMessage");
      setMessageSent(true);
      return;
    }
    
    // Method 3: Create and dispatch a custom event
    const event = new CustomEvent('sendChatMessage', { 
      detail: { message },
      bubbles: true 
    });
    document.dispatchEvent(event);
    console.log("Message sent using custom event");
    setMessageSent(true);
    
    // Method 4: Direct DOM manipulation - find the chat input and send button
    try {
      const chatInput = document.querySelector('.chat-input');
      const sendButton = document.querySelector('.send-button');
      
      if (chatInput && sendButton) {
        // Set the input value
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype, 'value'
        ).set;
        nativeInputValueSetter.call(chatInput, message);
        
        // Trigger input event
        const inputEvent = new Event('input', { bubbles: true });
        chatInput.dispatchEvent(inputEvent);
        
        // Click the send button
        sendButton.click();
        console.log("Message sent using DOM manipulation");
        setMessageSent(true);
      }
    } catch (error) {
      console.error("Error sending message via DOM manipulation:", error);
    }
  };
  
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedSubcategory(null);
    setMessageSent(false);
    
    // Reset form data when changing categories
    setFormData({
      brand: '',
      model: '',
      licensePlate: '',
      notes: '',
      preferredTime: '',
      customerType: 'private',
      timeRange: 'mattina',
      calendarDate: '',
      calendarTime: ''
    });
    
    // Focus on the first subcategory button after a short delay to allow rendering
    setTimeout(() => {
      const subcategoryButtons = document.querySelectorAll('.subcategory-button');
      if (subcategoryButtons && subcategoryButtons.length > 0) {
        subcategoryButtons[0].focus();
      }
    }, 100);
  };
  
  // Hardcoded prompts for information categories
  const getInfoPrompt = (categoryId, subcategoryId) => {
    const prompts = {
      'promotions': {
        'new-cars': 'Quali sono le promozioni attuali sulle auto nuove? Sono interessato a conoscere le offerte speciali, i finanziamenti agevolati e gli incentivi disponibili.',
        'used-cars': 'Vorrei informazioni sulle promozioni per auto usate. Avete veicoli in offerta speciale o programmi di finanziamento dedicati all\'usato?',
        'service-promo': 'Quali promozioni avete attualmente per i servizi di officina? Ci sono sconti su tagliandi, manutenzione o altri servizi?',
        'financing': 'Vorrei conoscere le opzioni di finanziamento disponibili. Quali tassi di interesse offrite e quali sono le condizioni per leasing e finanziamenti?'
      },
      'info': {
        'models': 'Quali modelli di auto avete attualmente disponibili in concessionaria? Sono interessato a conoscere le caratteristiche e i prezzi.',
        'prices': 'Vorrei ricevere informazioni sui prezzi e richiedere un preventivo. Potete fornirmi un listino aggiornato dei modelli disponibili?',
        'hours': 'Quali sono gli orari di apertura della concessionaria e dell\'officina? Vorrei anche sapere come posso contattarvi per un appuntamento.',
        'other-info': 'Vorrei ricevere informazioni generali sui vostri servizi. Cosa offrite ai clienti e quali sono i vantaggi di scegliere la vostra concessionaria?'
      }
    };
    
    return prompts[categoryId]?.[subcategoryId] || `Vorrei informazioni su: ${categoryId} - ${subcategoryId}`;
  };
  
  // Handle subcategory click
  const handleSubcategoryClick = (subcategory) => {
    setSelectedSubcategory(subcategory);
    
    // If no form is required, send the hardcoded prompt immediately
    if (selectedCategory && !selectedCategory.requiresForm) {
      const messageText = getInfoPrompt(selectedCategory.id, subcategory.id);
      handleSendMessage(messageText);
      
      // Reset selections after sending
      setTimeout(() => {
        setSelectedCategory(null);
        setSelectedSubcategory(null);
      }, 500);
      return;
    }
    
    // Focus on the first field after a short delay
    setTimeout(() => {
      const fields = subcategory.fields || [];
      
      // Focus on the appropriate first field
      if (fields.includes('brand') && brandInputRef.current) {
        brandInputRef.current.focus();
      } else if (fields.includes('notes') && notesInputRef.current) {
        notesInputRef.current.focus();
      } else if (fields.includes('calendarDate') && calendarDateRef.current) {
        calendarDateRef.current.focus();
      } else if (fields.includes('timeRange')) {
        const timeRangeInputs = document.querySelectorAll('input[name="timeRange"]');
        if (timeRangeInputs && timeRangeInputs.length > 0) {
          timeRangeInputs[0].focus();
        }
      }
    }, 100);
  };
  
  // Check if the form is valid before submission
  const isFormValid = () => {
    if (!selectedCategory || !selectedCategory.requiresForm || !selectedSubcategory) return false;
    
    const fields = selectedSubcategory.fields || [];
    
    // Check required fields
    if (fields.includes('brand') && !formData.brand.trim()) return false;
    
    // If brand is not required but model is, check model
    if (!fields.includes('brand') && fields.includes('model') && !formData.model.trim()) return false;
    
    // Check license plate for trade-in
    if (fields.includes('licensePlate') && selectedSubcategory.id === 'trade-in' && !formData.licensePlate.trim()) return false;
    
    // Check notes for other-visit
    if (fields.includes('notes') && selectedSubcategory.id === 'other-visit' && !formData.notes.trim()) return false;
    
    // Check calendar fields
    if (fields.includes('calendar') && (!formData.calendarDate || !formData.calendarTime)) return false;
    
    // Check time range field
    if (fields.includes('timeRange') && !formData.timeRange) return false;
    
    // Check preferred time if required
    if (fields.includes('preferredTime') && !formData.preferredTime.trim()) return false;
    
    return true;
  };
  
  // Get the next input field based on the current field
  const getNextInputField = (currentField) => {
    if (!selectedCategory || !selectedSubcategory) return null;
    
    const fields = selectedSubcategory.fields || [];
    const fieldOrder = [
      'brand', 
      'model', 
      'customerType', 
      'licensePlate', 
      'calendarDate',
      'calendarTime',
      'timeRange',
      'preferredTime', 
      'notes'
    ];
    
    // Find the index of the current field
    const currentIndex = fieldOrder.indexOf(currentField);
    
    // If not found or last field, return null
    if (currentIndex === -1 || currentIndex === fieldOrder.length - 1) return null;
    
    // Find the next field that is included in the form
    for (let i = currentIndex + 1; i < fieldOrder.length; i++) {
      if (fields.includes(fieldOrder[i])) {
        return fieldOrder[i];
      }
    }
    
    return null;
  };
  
  // Function to focus on the next input field
  const focusNextField = (currentField) => {
    const nextField = getNextInputField(currentField);
    
    if (!nextField) {
      // If there's no next field, focus on the submit button
      if (submitButtonRef.current && isFormValid()) {
        submitButtonRef.current.focus();
        // Optionally, auto-submit the form after a delay
        // setTimeout(() => submitButtonRef.current.click(), 500);
      }
      return;
    }
    
    // Focus on the next field
    switch (nextField) {
      case 'model':
        if (modelInputRef.current) modelInputRef.current.focus();
        break;
      case 'licensePlate':
        if (licensePlateInputRef.current) licensePlateInputRef.current.focus();
        break;
      case 'preferredTime':
        if (preferredTimeInputRef.current) preferredTimeInputRef.current.focus();
        break;
      case 'notes':
        if (notesInputRef.current) notesInputRef.current.focus();
        break;
      default:
        break;
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'radio' ? checked ? value : formData[name] : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // Add validation feedback
    if (type !== 'radio' && value.trim() !== '') {
      // Add visual feedback for completed field
      e.target.classList.add('field-valid');
      
      // Show checkmark icon
      const parentGroup = e.target.closest('.mia-form-group');
      if (parentGroup) {
        const existingIcon = parentGroup.querySelector('.field-valid-icon');
        if (!existingIcon) {
          const validIcon = document.createElement('span');
          validIcon.className = 'field-valid-icon material-icons';
          validIcon.textContent = 'check_circle';
          parentGroup.appendChild(validIcon);
        }
      }
      
      // Remove automatic field progression - we'll only progress when user presses Enter or Tab
      // The focusNextField function will be called from handleInputKeyDown instead
    } else if (type !== 'radio' && value.trim() === '') {
      // Remove validation if field is emptied
      e.target.classList.remove('field-valid');
      
      // Remove checkmark icon
      const parentGroup = e.target.closest('.mia-form-group');
      if (parentGroup) {
        const existingIcon = parentGroup.querySelector('.field-valid-icon');
        if (existingIcon) {
          parentGroup.removeChild(existingIcon);
        }
      }
    }
  };
  
  // Handle input completion (when user presses Tab or Enter)
  const handleInputKeyDown = (e, fieldName) => {
    e.stopPropagation();
    
    // If Tab is pressed, let the default behavior handle it
    if (e.key === 'Tab') {
      // Don't prevent default - let the browser handle tab navigation
      // But we'll still call focusNextField to handle special cases
      if (!e.shiftKey) { // Only for forward tab, not shift+tab
        focusNextField(fieldName);
      }
      return;
    }
    
    // If Enter is pressed, move to the next field or submit the form
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault(); // Prevent form submission
      
      // Get the next field
      const nextField = getNextInputField(fieldName);
      
      // If there's no next field, try to submit the form
      if (!nextField) {
        if (isFormValid() && submitButtonRef.current) {
          submitButtonRef.current.click();
        }
        return;
      }
      
      // For calendar fields, handle special case
      if (fieldName === 'calendarDate' && nextField === 'calendarTime') {
        // Wait for available times to be generated
        setTimeout(() => {
          calendarTimeRef.current?.focus();
        }, 100);
        return;
      }
      
      // For other fields, focus on the next field
      focusNextField(fieldName);
    }
  };
  
  const handleInputFocus = (e) => {
    e.stopPropagation();
    // Set this input as the active input
    activeInputRef.current = e.target;
    
    // Disable the focus interval in ChatFooter
    if (window.focusIntervalRef && window.focusIntervalRef.current) {
      clearInterval(window.focusIntervalRef.current);
      window.focusIntervalRef.current = null;
    }
  };
  
  // Generate a dynamic prompt based on the selected category and subcategory
  const generateDynamicPrompt = () => {
    if (!selectedCategory || !selectedSubcategory) return '';
    
    let prompt = '';
    
    // Add category and subcategory information
    prompt += `Categoria: ${selectedCategory.title}, Sottocategoria: ${selectedSubcategory.title}\n`;
    
    // Add form data if available
    if (selectedCategory.requiresForm) {
      if (formData.brand) prompt += `Marca: ${formData.brand}\n`;
      if (formData.model) prompt += `Modello: ${formData.model}\n`;
      if (formData.licensePlate) prompt += `Targa: ${formData.licensePlate}\n`;
      if (formData.customerType) prompt += `Tipo cliente: ${formData.customerType === 'private' ? 'Privato' : 'Azienda'}\n`;
      
      // Add new fields
      if (formData.timeRange) {
        prompt += `Fascia oraria: ${formData.timeRange === 'mattina' ? 'Mattina (9:00 - 13:00)' : 'Pomeriggio (14:00 - 18:00)'}\n`;
      }
      
      if (formData.calendarDate && formData.calendarTime) {
        prompt += `Data e ora appuntamento: ${formData.calendarDate} alle ${formData.calendarTime}\n`;
      }
      
      if (formData.preferredTime) prompt += `Orario desiderato: ${formData.preferredTime}\n`;
      if (formData.notes) prompt += `Note: ${formData.notes}\n`;
    }
    
    return prompt;
  };
  
  // Handle form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Validate the form
    if (!isFormValid()) {
      console.log("Form is not valid");
      return;
    }
    
    // Generate the prompt based on the selected category and subcategory
    const prompt = generateDynamicPrompt();
    
    // Create a formatted message for display
    let formattedMessage = '';
    
    if (selectedCategory.id === 'dealership') {
      formattedMessage = `Richiesta di appuntamento in concessionaria per ${selectedSubcategory.title.toLowerCase()}`;
      
      if (formData.brand && formData.model) {
        formattedMessage += ` di ${formData.brand} ${formData.model}`;
      } else if (formData.brand) {
        formattedMessage += ` di ${formData.brand}`;
      }
      
      if (formData.calendarDate && formData.calendarTime) {
        const date = new Date(formData.calendarDate);
        const formattedDate = date.toLocaleDateString('it-IT', { 
          weekday: 'long', 
          day: 'numeric', 
          month: 'long', 
          year: 'numeric' 
        });
        formattedMessage += ` per il giorno ${formattedDate} alle ore ${formData.calendarTime}`;
      }
    } else if (selectedCategory.id === 'workshop') {
      formattedMessage = `Richiesta di appuntamento in officina per ${selectedSubcategory.title.toLowerCase()}`;
      
      if (formData.brand && formData.model) {
        formattedMessage += ` di ${formData.brand} ${formData.model}`;
      } else if (formData.brand) {
        formattedMessage += ` di ${formData.brand}`;
      }
      
      if (formData.licensePlate) {
        formattedMessage += ` (targa: ${formData.licensePlate})`;
      }
      
      if (formData.timeRange) {
        formattedMessage += ` in fascia ${formData.timeRange === 'mattina' ? 'mattutina (9:00-13:00)' : 'pomeridiana (14:00-18:00)'}`;
      }
    }
    
    // Send the message
    handleSendMessage(prompt);
    
    // Set message sent to true to show success message
    setMessageSent(true);
    
    // Log the form submission
    console.log("Form submitted:", {
      category: selectedCategory.title,
      subcategory: selectedSubcategory.title,
      formData,
      prompt
    });
    
    // Reset selections after a delay
    setTimeout(() => {
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setFormData({
        brand: '',
        model: '',
        licensePlate: '',
        notes: '',
        preferredTime: '',
        customerType: 'private',
        timeRange: 'mattina',
        calendarDate: '',
        calendarTime: ''
      });
      
      // Clear the active input reference
      activeInputRef.current = null;
      
    }, 5000);
  };
  
  // Add click handler to stop propagation for all form elements
  const handleFormClick = (e) => {
    e.stopPropagation();
  };
  
  // Prevent keyboard events from bubbling up
  const handleFormKeyDown = (e) => {
    e.stopPropagation();
  };
  
  // Render the category icons
  const renderCategories = () => {
    if (!categories || categories.length === 0) return null;
    
    return (
      <div className="message-categories">
        {categories.map(category => (
          <button
            key={category.id}
            className={`category-button ${selectedCategory && selectedCategory.id === category.id ? 'active' : ''}`}
            onClick={() => handleCategoryClick(category)}
            aria-label={category.title}
          >
            <span className="category-icon material-icons">{category.icon}</span>
            <span className="category-title">{category.title}</span>
          </button>
        ))}
      </div>
    );
  };
  
  // Add back button handler
  const handleBackClick = () => {
    if (selectedSubcategory) {
      // If we're in a form or subcategory, go back to subcategories
      setSelectedSubcategory(null);
                            // Focus on the previously selected subcategory        setTimeout(() => {          const subcategoryButtons = document.querySelectorAll('.subcategory-button');          if (subcategoryButtons && subcategoryButtons.length > 0) {            subcategoryButtons[0].focus();          }        }, 100);
    } else if (selectedCategory) {
      // If we're in subcategories, go back to categories
      setSelectedCategory(null);
                      // Focus on the previously selected category        setTimeout(() => {          const categoryButtons = document.querySelectorAll('.category-button');          if (categoryButtons && categoryButtons.length > 0) {            categoryButtons[0].focus();          }        }, 100);
    }
  };
  
  // Modify renderSubcategories to include back button
  const renderSubcategories = () => {
    if (!selectedCategory || !selectedCategory.subcategories) return null;
    
    const subcategories = selectedCategory.subcategories || [];
    const itemCount = subcategories.length;
    const gridClassName = `subcategories-grid items-${itemCount}`;
    
    return (
      <div className="subcategories-container">
        <div className="subcategories-header">
          <button 
            className="back-button" 
            onClick={handleBackClick}
            aria-label="Torna indietro"
          >
            <span className="material-icons">arrow_back</span>
            <span>Indietro</span>
          </button>
          <div className="subcategories-title">
            <span className="material-icons">{selectedCategory.icon}</span>
            <h3>{selectedCategory.title}</h3>
          </div>
        </div>
        <div className={gridClassName}>
          {selectedCategory.subcategories.map(subcategory => (
            <button
              key={subcategory.id}
              className={`subcategory-button ${selectedSubcategory && selectedSubcategory.id === subcategory.id ? 'active' : ''}`}
              onClick={() => handleSubcategoryClick(subcategory)}
            >
              {subcategory.title}
            </button>
          ))}
        </div>
      </div>
    );
  };
  
  // Modify renderForm to include back button
  const renderForm = () => {
    if (!selectedCategory || !selectedCategory.requiresForm || !selectedSubcategory) return null;
    
    if (messageSent) {
      return (
        <div className="form-success-message">
          <span className="material-icons success-icon">check_circle</span>
          <h3>Richiesta inviata!</h3>
          <p>La tua richiesta è stata inviata con successo. L'assistente risponderà a breve.</p>
        </div>
      );
    }
    
    const fields = selectedSubcategory.fields || [];
    
    return (
      <form 
        className="appointment-form" 
        onSubmit={handleFormSubmit} 
        onClick={handleFormClick}
        onKeyDown={handleFormKeyDown}
        ref={formRef}
      >
        <div className="form-header">
          <button 
            type="button"
            className="back-button" 
            onClick={handleBackClick}
            aria-label="Torna indietro"
          >
            <span className="material-icons">arrow_back</span>
            <span>Indietro</span>
          </button>
          <h3>Compila i dettagli</h3>
          <p className="form-note">
            Nota: questo è solo il primo passo per la prenotazione. Dopo l'invio, sarai ricontattato per confermare l'appuntamento.
            {selectedCategory.id === 'dealership' && fields.includes('calendar') && 
              " L'orario selezionato è una preferenza e verrà confermato in base alla disponibilità."}
            {selectedCategory.id === 'workshop' && fields.includes('timeRange') && 
              " La fascia oraria selezionata è indicativa e verrà confermata in base alla disponibilità."}
          </p>
        </div>
        
        {/* Brand field */}
        {fields.includes('brand') && (
          <div className="mia-aitomotivelab-form-group">
            <label htmlFor="brand">Marca*</label>
            <input
              type="text"
              id="brand"
              name="brand"
              value={formData.brand}
              onChange={handleInputChange}
              onKeyDown={(e) => handleInputKeyDown(e, 'brand')}
              onFocus={handleInputFocus}
              required
              placeholder="Es. Fiat, Alfa Romeo, ecc."
              className="mia-form-control"
              autoComplete="off"
              ref={brandInputRef}
            />
          </div>
        )}
        
        {/* Model field */}
        {fields.includes('model') && (
          <div className="mia-aitomotivelab-form-group">
            <label htmlFor="model">Modello{!fields.includes('brand') && '*'}</label>
            <input
              type="text"
              id="model"
              name="model"
              value={formData.model}
              onChange={handleInputChange}
              onKeyDown={(e) => handleInputKeyDown(e, 'model')}
              onFocus={handleInputFocus}
              placeholder="Es. 500, Giulia, ecc."
              required={!fields.includes('brand')}
              className="mia-form-control"
              autoComplete="off"
              ref={modelInputRef}
            />
          </div>
        )}
        
        {/* Customer type field */}
        {fields.includes('customerType') && (
          <div className="mia-aitomotivelab-form-group">
            <label>Tipo cliente*</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="customerType"
                  value="private"
                  checked={formData.customerType === 'private'}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onKeyDown={(e) => handleInputKeyDown(e, 'customerType')}
                />
                Privato
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="customerType"
                  value="company"
                  checked={formData.customerType === 'company'}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onKeyDown={(e) => handleInputKeyDown(e, 'customerType')}
                />
                Azienda
              </label>
            </div>
          </div>
        )}
        
        {/* License plate field */}
        {fields.includes('licensePlate') && (
          <div className="mia-aitomotivelab-form-group">
            <label htmlFor="licensePlate">Targa{selectedSubcategory.id === 'trade-in' && '*'}</label>
            <input
              type="text"
              id="licensePlate"
              name="licensePlate"
              value={formData.licensePlate}
              onChange={handleInputChange}
              onKeyDown={(e) => handleInputKeyDown(e, 'licensePlate')}
              onFocus={handleInputFocus}
              placeholder="Es. AB123CD"
              required={selectedSubcategory.id === 'trade-in'}
              className="mia-form-control"
              autoComplete="off"
              ref={licensePlateInputRef}
            />
          </div>
        )}
        
        {/* Time range field */}
        {fields.includes('timeRange') && (
          <div className="mia-aitomotivelab-form-group">
            <label htmlFor="timeRange">Fascia oraria preferita*</label>
            <div className="time-range-selector">
              <label className="time-range-option">
                <input
                  type="radio"
                  name="timeRange"
                  value="mattina"
                  checked={formData.timeRange === 'mattina'}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onKeyDown={(e) => handleInputKeyDown(e, 'timeRange')}
                  required
                />
                <span className="time-range-label">
                  <span className="material-icons">wb_sunny</span>
                  Mattina (9:00 - 13:00)
                </span>
              </label>
              <label className="time-range-option">
                <input
                  type="radio"
                  name="timeRange"
                  value="pomeriggio"
                  checked={formData.timeRange === 'pomeriggio'}
                  onChange={handleInputChange}
                  onFocus={handleInputFocus}
                  onKeyDown={(e) => handleInputKeyDown(e, 'timeRange')}
                  required
                />
                <span className="time-range-label">
                  <span className="material-icons">wb_twilight</span>
                  Pomeriggio (14:00 - 18:00)
                </span>
              </label>
            </div>
            <p className="field-help-text">La fascia oraria selezionata è indicativa e verrà confermata in base alla disponibilità.</p>
          </div>
        )}
        
        {/* Calendar field */}
        {fields.includes('calendar') && (
          <div className="mia-aitomotivelab-form-group calendar-group">
            <label htmlFor="calendarDate">Data e ora dell'appuntamento*</label>
            <div className="calendar-container">
              <div className="calendar-date-picker">
                <label htmlFor="calendarDate">Data*</label>
                <input
                  type="date"
                  id="calendarDate"
                  name="calendarDate"
                  value={formData.calendarDate}
                  onChange={(e) => {
                    handleInputChange(e);
                    setSelectedDate(e.target.value);
                  }}
                  onKeyDown={(e) => handleInputKeyDown(e, 'calendarDate')}
                  onFocus={handleInputFocus}
                  required
                  className="mia-form-control"
                  min={new Date().toISOString().split('T')[0]}
                  ref={calendarDateRef}
                />
              </div>
              
              {formData.calendarDate && (
                <div className="calendar-time-picker">
                  <label htmlFor="calendarTime">Orario*</label>
                  <select
                    id="calendarTime"
                    name="calendarTime"
                    value={formData.calendarTime}
                    onChange={handleInputChange}
                    onKeyDown={(e) => handleInputKeyDown(e, 'calendarTime')}
                    onFocus={handleInputFocus}
                    required
                    className="mia-form-control"
                    ref={calendarTimeRef}
                  >
                    <option value="">Seleziona un orario</option>
                    {availableTimes.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <p className="field-help-text">L'orario selezionato è una preferenza e verrà confermato in base alla disponibilità.</p>
          </div>
        )}
        
        {/* Preferred time field (legacy) */}
        {fields.includes('preferredTime') && (
          <div className="mia-aitomotivelab-form-group">
            <label htmlFor="preferredTime">Orario desiderato*</label>
            <input
              type="text"
              id="preferredTime"
              name="preferredTime"
              value={formData.preferredTime}
              onChange={handleInputChange}
              onKeyDown={(e) => handleInputKeyDown(e, 'preferredTime')}
              onFocus={handleInputFocus}
              placeholder="Es. Lunedì pomeriggio, Martedì mattina, ecc."
              required
              className="mia-form-control"
              autoComplete="off"
              ref={preferredTimeInputRef}
            />
          </div>
        )}
        
        {/* Notes field - always displayed at the end */}
        {fields.includes('notes') && (
          <div className="mia-aitomotivelab-form-group">
            <label htmlFor="notes">Note{selectedSubcategory.id === 'other-visit' && '*'}</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Tab') {
                  e.preventDefault();
                  focusNextField('notes');
                }
              }}
              onFocus={handleInputFocus}
              placeholder="Inserisci eventuali note o richieste specifiche"
              required={selectedSubcategory.id === 'other-visit'}
              className="mia-form-control"
              rows="3"
              autoComplete="off"
              ref={notesInputRef}
            />
          </div>
        )}
        
        <div className="form-actions">
          <button 
            type="submit" 
            className="submit-button"
            ref={submitButtonRef}
          >
            Invia richiesta
          </button>
        </div>
        
        <div className="mia-form-disclaimer">
          <p>Dopo l'invio della richiesta, sarai ricontattato per confermare i dettagli dell'appuntamento.</p>
        </div>
      </form>
    );
  };

  // Render phone input tool for second engagement
  const renderPhoneInputTool = () => {
    if (phoneSubmitted) {
      return (
        <div className="mia-phone-input-success">
          <div className="success-icon">✓</div>
          <p>Grazie! Abbiamo ricevuto il suo numero di telefono. La ricontatteremo presto.</p>
        </div>
      );
    }

    return (
      <div className="mia-phone-input-container">
        <form onSubmit={handlePhoneSubmit} className="mia-phone-input-form">
          <div className="mia-phone-input-field">
            <input
              type="tel"
              placeholder="Inserisci il tuo numero di telefono"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="mia-phone-input"
              required
            />
            <button 
              type="submit" 
              className="mia-phone-submit-btn"
              disabled={!phoneNumber.trim()}
            >
              Invia
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Render WhatsApp CTA for second engagement
  const renderWhatsAppCTA = () => {
    return (
      <div className="mia-whatsapp-cta-container">
        <button 
          onClick={handleWhatsAppClick}
          className="mia-whatsapp-cta-button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
          </svg>
          Continua su WhatsApp
        </button>
      </div>
    );
  };
  
  // Determine if this is a short message (1-3 words)
  const isShortMessage = message.text && 
    message.text.trim().split(/\s+/).length <= 3 && 
    message.text.length < 30;

  // Build message classes with proper mia- prefixes
  const messageClasses = ['mia-message-bubble'];
  
  if (sender === 'user') {
    messageClasses.push('mia-user-message');
    if (isShortMessage) {
      messageClasses.push('short-text');
    }
  } else if (sender === 'bot' || message.id === 'welcome') {
    messageClasses.push('mia-bot-message');
    if (isHumanControlled) {
      messageClasses.push('human-operator');
    }
  } else if (type) {
    messageClasses.push(`mia-${type}-message`);
  }
  
  // Add special class for messages with carousels
  const hasCarousel = carouselItems && carouselItems.length > 0;
  if (hasCarousel) {
    messageClasses.push('has-carousel');
  }
  
  // Add special class for welcome message
  if (message.id === 'welcome') {
    messageClasses.push('mia-system-message');
  }
  
  if (className) {
    messageClasses.push(className);
  }
  
  // Add a debug log for message properties
  useEffect(() => {
    if (message.id === 'welcome' || type === 'system') {
      console.log('Processing welcome/system message:', {
        id: message.id,
        type,
        hasHTML: /<\/?[a-z][\s\S]*>/i.test(text || ''),
        textLength: (text || '').length
      });
    }
  }, [message.id, type, text]);

  // Combine refs
  const combinedRef = (node) => {
    messageBubbleRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  return (
    <div 
      ref={combinedRef}
      className={`${messageClasses.join(' ')} ${isSwiping ? 'swiping' : ''}`}
      tabIndex="0"
      aria-label={`${sender === 'user' ? 'Your message' : 'Bot message'}: ${text ? text.replace(/<[^>]*>/g, '') : ''}`}
      role="article"
      data-message-type={message.type || undefined}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: isMobileRef.current && sender === 'bot' && !carouselItems?.length ? `translateX(${swipeOffset}px)` : 'none',
        transition: isSwiping ? 'none' : 'transform 0.2s ease-out'
      }}
    >
      {/* Reply indicator - shows when swiping */}
      {isMobileRef.current && sender === 'bot' && !carouselItems?.length && (
        <div 
          className="mia-swipe-reply-indicator"
          style={{
            opacity: Math.min(1, Math.abs(swipeOffset) / SWIPE_THRESHOLD),
            transform: `translateX(${Math.max(0, swipeOffset + 70)}px) translateY(-50%)`
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 9V5L3 12L10 19V14.9C15 14.9 18.5 16.5 21 20C20 15 17 10 10 9Z" fill="currentColor"/>
          </svg>
          <span>Rispondi</span>
        </div>
      )}
      
      {/* Reply context - shows which message this is replying to (only for user messages) */}
      {replyTo && sender === 'user' && replyTo.text && (
        <div className="mia-message-reply-context">
          <div className="mia-reply-context-indicator"></div>
          <div className="mia-reply-context-content">
            <div className="mia-reply-context-sender">
              {replyTo.sender === 'bot' ? 'MIA' : 'Tu'}
            </div>
            <div className="mia-reply-context-text">
              {replyTo.text.length > 40 
                ? replyTo.text.substring(0, 40) + '...' 
                : replyTo.text}
            </div>
          </div>
        </div>
      )}
      
      {/* Special handling for system messages and welcome message */}
      {(type === 'system' || message.id === 'welcome') ? (
        <div className="message-content" dangerouslySetInnerHTML={{ __html: text }} />
      ) : useMarkdown ? (
        <div className="message-content">
          <ReactMarkdown
            components={{
              a: CustomLink,
              p: ({ node, ...props }) => <p {...props} />,
              ul: ({ node, ...props }) => <ul className="formatted-list" {...props} />,
              ol: ({ node, ...props }) => <ol className="formatted-list" {...props} />,
              li: ({ node, ...props }) => <li {...props} />,
              h1: ({ node, ...props }) => <h1 {...props} />,
              h2: ({ node, ...props }) => <h2 {...props} />,
              h3: ({ node, ...props }) => <h3 {...props} />
            }}
          >
            {text || ''}
          </ReactMarkdown>
        </div>
      ) : (
        <div className="message-content" dangerouslySetInnerHTML={{ __html: formatText(text) }} />
      )}
      {timestamp && <div className="mia-message-timestamp">{formatWhatsAppTimestamp(timestamp)}</div>}
      
      {carouselItems && carouselItems.length > 0 && (
        <div className="mia-message-with-carousel">
          <div className="mia-full-width-carousel">
            <CustomCarousel items={carouselItems} />
          </div>
        </div>
      )}
      
      {/* Only render categories if not a welcome message */}
      {message.id !== 'welcome' && categories && categories.length > 0 && !selectedCategory && renderCategories()}
      {message.id !== 'welcome' && selectedCategory && !selectedSubcategory && renderSubcategories()}
      {message.id !== 'welcome' && selectedCategory && selectedCategory.requiresForm && selectedSubcategory && !messageSent && renderForm()}

      {/* Render second engagement tools based on message type */}
      {message.type === 'second-engagement-phone' && message.showPhoneInput && renderPhoneInputTool()}
      {message.type === 'second-engagement-whatsapp' && message.showWhatsAppCTA && renderWhatsAppCTA()}
    </div>
  );
});

// Wrap with React.memo to prevent unnecessary re-renders
export default React.memo(MessageBubble); 